import { useCallback, useState } from 'react';
import {
  BULK_EDIT_ACCOUNT_TYPES, BULK_EDIT_ATTRIBUTE_PATH, BULK_EDIT_ATTRIBUTE_REVIEW_PATH, BULK_EDIT_IMAGE_PATH, BULK_EDIT_LABEL_KEYS, BULK_EDIT_PATH, BULK_EDIT_SUPPLIER_PATH, BULK_EDIT_SUPPLIER_STATUSES, EDIT_FIELDS, LIST_SELECTION_MAX_ROWS, Size, accountText, bulkEditSupplierText, formatCurrency, isBulkEditPriceField, listSelectionCapSentence, listSelectionTooManySentence, statusDefinitionSeed, t,
  type BulkEditAnswer, type BulkEditAttributeReview, type BulkEditField, type BulkEditRecordType, type BulkEditSupplierStatus, type EditField, type EditOptionSource, type ListFilterSelection, type SuggestionRow,
} from '@bms2/shared';
import { Button, Dropdown, NumericInput, RadioGroup, Select, TextInput } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { ACCOUNTS_PATH } from '../accounting/api';
import { ATTRIBUTE_TYPES_PATH, useCompanySearch } from '../settings/api';
import { apiFetch, post, upload, useApi } from './api';
import { useAddNew } from './AddNewHost';
import { roleCandidatesPath } from './LeadModals';
import { ItemImageModal } from './ItemModals';
import { Field as RecordField, ITEM_OPTIONS_PATH } from './RecordFields';
import styles from './RecordPage.module.css';

/** A refused row reads as its name, then the single edit's reason; a bare glyph in JSX is a literal the lint refuses. */
const REASON_SEPARATOR = ': ';

/** The Account Roles panel's own Sales Person, whose picker offers everybody (piece 20). */
const SALES_PERSON_ROLE = 'sales-person';

/** The four Item statuses, labelled as the status pills label them. */
const STATUS_OPTIONS = statusDefinitionSeed.filter((row) => row.entityType === 'ItemStatus').map((row) => ({ value: row.statusValue, label: row.label }));

/** The Item form's own fields, by key: an Item field Edit Selected draws through the form's own picker (decision 2890). */
const ITEM_FORM_FIELDS = new Map(EDIT_FIELDS.Item.map((field) => [field.key, field]));

type Draft = { value: unknown; text: string | null };
/** The picture Set image holds until Change Them sends it, scaled by the Item Image modal as a single Set image scales it (decision 7640). */
type Picture = { blob: Blob; name: string };
type ImageMode = 'set' | 'remove';
/** A catalog attribute type as Settings lists it, with its values; the families beside it hold each Measured type's units (decision 7790). */
type AttributeTypeRow = { id: string; attributeName: string; kind: string; isActive: boolean; unitFamilyId: string | null; defaultUnitId: string | null; values: { id: string; value: string; isActive: boolean }[] };
type AttributeTypes = { rows: AttributeTypeRow[]; families: { id: string; units: { id: string; label: string }[] }[] };
/** The attribute the window holds: the type, and the value in the type's kind, a number with its unit or one of its values (decision 7790). */
type AttributeDraft = { typeId: string; unitId: string; entered: string; valueId: string };
const NO_ATTRIBUTE: AttributeDraft = { typeId: '', unitId: '', entered: '', valueId: '' };

/** The two statuses a bulk-added supplier takes, labelled as the Sourcing tile's status control labels them (decision 8780). */
const SUPPLIER_STATUS_OPTIONS = BULK_EDIT_SUPPLIER_STATUSES.map((status) => ({ value: status, label: t(status === 'Primary' ? 'item.supplier.status.primary' : 'item.supplier.status.active') }));

/** The noop the Item Image modal's save callback takes in pick mode, where nothing is saved by the window itself. */
const NOTHING_SAVED = (): void => undefined;
type ItemOptions = Record<EditOptionSource, { value: string; label: string; hint?: string | null }[]>;
type AccountRow = { id: string; accountNumber: string; name: string; type: string; isActive: boolean };

export interface BulkEditModalProps {
  open: boolean;
  recordType: BulkEditRecordType;
  fields: readonly BulkEditField[];
  ids: readonly string[];
  /**
   * 🔒 Over Select all N matching, the list's filter with its exclusions, sent in place of the ids, and
   * how many rows it names, which the head reads before Apply (decision 7870). The server resolves it.
   */
  selection?: { value: ListFilterSelection; count: number } | null;
  apiBaseUrl?: string;
  onCancel: () => void;
  /** Called once the rows are written, so the list reads its rows again. */
  onApplied: (answer: BulkEditAnswer) => void;
}

type Step = 'edit' | 'confirm' | 'result';

/** The Item form field Edit Selected draws for a field, where the form has one; a price, the status and the two accounts are drawn here. */
function formFieldOf(recordType: BulkEditRecordType, field: BulkEditField): EditField | null {
  if (recordType !== 'Item' || isBulkEditPriceField(field) || field === 'status') return null;
  return ITEM_FORM_FIELDS.get(field) ?? null;
}

function isAccountField(field: BulkEditField): field is 'salesAccountId' | 'purchaseAccountId' {
  return field === 'salesAccountId' || field === 'purchaseAccountId';
}

/**
 * 🔒 Edit Selected (piece 13; decisions 0982, 2890): the one field and its new
 * value, then a confirmation naming the count, then the answer — the rows
 * written and every row refused with its reason. 🔨 The window offers only the
 * fields the list declares, each through the picker the Item form draws for it,
 * and the server refuses any other field by name. The two accounts offer the
 * live accounts of the types each takes, as Change Accounts did before it
 * folded in here.
 */
export function BulkEditModal({ open, recordType, fields, ids, selection = null, apiBaseUrl = '', onCancel, onApplied }: BulkEditModalProps) {
  const [step, setStep] = useState<Step>('edit');
  const [field, setField] = useState<BulkEditField>(fields[0]!);
  const [drafts, setDrafts] = useState<Partial<Record<BulkEditField, Draft>>>({});
  const [error, setError] = useState<string | null>(null);
  const [answer, setAnswer] = useState<BulkEditAnswer | null>(null);
  const [busy, setBusy] = useState(false);
  const [imageMode, setImageMode] = useState<ImageMode>('set');
  const [picture, setPicture] = useState<Picture | null>(null);
  const [picking, setPicking] = useState(false);
  const [attr, setAttr] = useState<AttributeDraft>(NO_ATTRIBUTE);
  const [reviewed, setReviewed] = useState<BulkEditAttributeReview | null>(null);
  /** 🔒 The supplier the window adds: a company from the Sourcing tile's own picker, at Primary or Active, Active preselected (decision 8780). */
  const [vendor, setVendor] = useState<SuggestionRow | null>(null);
  const [vendorStatus, setVendorStatus] = useState<BulkEditSupplierStatus>('Active');
  const searchCompanies = useCompanySearch(apiBaseUrl);
  const addNew = useAddNew(apiBaseUrl);
  const label = t(BULK_EDIT_LABEL_KEYS[field]);
  const formField = formFieldOf(recordType, field);
  const itemOptions = useApi<ItemOptions>(formField?.optionsFrom ? ITEM_OPTIONS_PATH : null, apiBaseUrl);
  const lists: Partial<ItemOptions> = itemOptions.state.kind === 'ready' ? itemOptions.state.data : {};
  /** 🔨 Only a live account of a type the field takes is offered (decisions 0478, 2890); the route refuses any other before a row is written. */
  const accounts = useApi<{ rows: AccountRow[] }>(isAccountField(field) ? ACCOUNTS_PATH : null, apiBaseUrl, { cache: true });
  const accountOptions = isAccountField(field) && accounts.state.kind === 'ready'
    ? accounts.state.data.rows.filter((row) => row.isActive && BULK_EDIT_ACCOUNT_TYPES[field].includes(row.type)).map((row) => ({ value: row.id, label: accountText(row) ?? row.id }))
    : [];
  /** 🔒 The catalog's own attribute types, the ones with no appliesTo, and the units of each family (decision 7790). */
  const attributeTypes = useApi<AttributeTypes>(field === 'attribute' ? ATTRIBUTE_TYPES_PATH : null, apiBaseUrl);
  const typeRows = attributeTypes.state.kind === 'ready' ? attributeTypes.state.data.rows.filter((row) => row.isActive && (row.kind === 'Measured' || row.kind === 'Enumerated')) : [];
  const attrType = typeRows.find((row) => row.id === attr.typeId) ?? null;
  const attrUnits = attributeTypes.state.kind === 'ready' ? (attributeTypes.state.data.families.find((family) => family.id === attrType?.unitFamilyId)?.units ?? []) : [];
  const search = useCallback(async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
    const response = await apiFetch(`${apiBaseUrl}${roleCandidatesPath(SALES_PERSON_ROLE, query)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { rows?: { id: string; label: string }[] };
    return (Array.isArray(body.rows) ? body.rows : []).map((row) => ({ id: row.id, line1: row.label }));
  }, [apiBaseUrl]);

  const draft = drafts[field];
  const setDraft = (next: Draft) => setDrafts((all) => ({ ...all, [field]: next }));
  /** A form field's choice, named in the words its picker shows; a company's name arrives from the pick itself. */
  const chooseFormValue = (form: EditField, next: unknown) => {
    const options = form.optionsFrom ? (lists[form.optionsFrom] ?? []) : (form.options ?? []);
    const text = form.kind === 'company'
      ? null
      : form.kind === 'boolean'
        ? (next === true ? t('record.yes') : t('record.no'))
        : form.kind === 'number'
          ? (typeof next === 'number' ? `${next}%` : null)
          : options.find((option) => option.value === next)?.label ?? null;
    setDrafts((all) => ({ ...all, [field]: { value: next, text: form.kind === 'company' ? all[field]?.text ?? null : text } }));
  };

  /** The value as the route takes it, with the words the confirmation reads it in; null while nothing is chosen. */
  const chosen = (): { value: number | string | boolean; text: string } | null => {
    if (field === 'attribute') {
      if (!attrType) return null;
      if (attrType.kind === 'Enumerated') {
        const picked = attrType.values.find((row) => row.id === attr.valueId);
        return picked ? { value: attrType.id, text: picked.value } : null;
      }
      const number = Number(attr.entered);
      const unit = attrUnits.find((row) => row.id === attr.unitId);
      return attr.entered.trim() !== '' && Number.isFinite(number) && unit ? { value: attrType.id, text: `${attr.entered.trim()} ${unit.label}` } : null;
    }
    if (field === 'supplier') return vendor ? { value: vendor.id, text: bulkEditSupplierText(vendor.line1, vendorStatus) } : null;
    if (field === 'imageFileId') return imageMode === 'remove' ? { value: '', text: t('bulkEdit.image.removed') } : picture ? { value: picture.name, text: picture.name } : null;
    if (formField?.kind === 'boolean') return { value: draft?.value === true, text: draft?.value === true ? t('record.yes') : t('record.no') };
    if (!draft || draft.value === null || draft.value === undefined || draft.value === '') return null;
    if (isBulkEditPriceField(field)) return { value: Number(draft.value) / 100, text: formatCurrency({ amount: Number(draft.value), currencyCode: 'USD' }) };
    return { value: draft.value as number | string, text: draft.text ?? String(draft.value) };
  };

  /** 🔒 The rows every route takes: the ticked ids, or the filter selection in their place (decision 7870). */
  const target = selection ? { selection: { query: selection.value.query, exclusions: [...selection.value.exclusions] } } : { ids: [...ids] };
  const rowCount = selection ? selection.count : ids.length;
  /** 🔨 A selection past the ceiling is refused whole, so the window says so and never sends it (decision 7870). */
  const overCap = selection !== null && rowCount > LIST_SELECTION_MAX_ROWS;
  /** The attribute route's body: the type and the value in its kind, beside the ticked rows (decision 7790). */
  const attributeBody = () => attrType?.kind === 'Enumerated'
    ? { ...target, attributeTypeId: attr.typeId, attributeTypeValueId: attr.valueId }
    : { ...target, attributeTypeId: attr.typeId, unitId: attr.unitId, enteredValue: Number(attr.entered) };
  const review = async () => {
    setError(null);
    if (overCap) return setError(listSelectionTooManySentence(rowCount));
    if (!chosen()) return setError(t('bulkEdit.needsValue'));
    if (field === 'attribute') {
      // 🔒 Review names the ticked rows whose category carries the attribute and those whose category does not, before anything is written (decision 7790).
      setBusy(true);
      const outcome = await post<BulkEditAttributeReview>(BULK_EDIT_ATTRIBUTE_REVIEW_PATH, { ...target, attributeTypeId: attr.typeId }, apiBaseUrl);
      setBusy(false);
      if (!outcome.ok) return setError(outcome.message);
      setReviewed(outcome.data);
    }
    setStep('confirm');
  };
  /** 🔒 The picture goes once, with the ids it is set on; no picture is Remove image (decision 7640). */
  const imageForm = (): FormData => {
    const form = new FormData();
    if (selection) form.append('selection', JSON.stringify({ query: selection.value.query, exclusions: [...selection.value.exclusions] }));
    else form.append('ids', JSON.stringify([...ids]));
    // 🔒 The mode rides with the picture, so the server never reads a missing picture as Remove image (decision 7871).
    form.append('mode', imageMode);
    if (imageMode === 'set' && picture) form.append('file', picture.blob, picture.name);
    return form;
  };
  const apply = async () => {
    const value = chosen();
    if (!value || busy) return;
    setBusy(true);
    const outcome = field === 'imageFileId'
      ? await upload<BulkEditAnswer>(BULK_EDIT_IMAGE_PATH, imageForm(), apiBaseUrl)
      : field === 'attribute'
        ? await post<BulkEditAnswer>(BULK_EDIT_ATTRIBUTE_PATH, attributeBody(), apiBaseUrl)
        : field === 'supplier'
          ? await post<BulkEditAnswer>(BULK_EDIT_SUPPLIER_PATH, { ...target, companyId: value.value, status: vendorStatus }, apiBaseUrl)
          : await post<BulkEditAnswer>(BULK_EDIT_PATH, { recordType, field, value: value.value, ...target }, apiBaseUrl);
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return setStep('edit');
    }
    setAnswer(outcome.data);
    setStep('result');
    onApplied(outcome.data);
  };

  const editAgain = () => {
    setAnswer(null);
    setStep('edit');
  };
  const count = `${rowCount} ${rowCount === 1 ? t('bulkEdit.row') : t('bulkEdit.rows')}`;
  /** 🔒 The head reads the count the edit will change, and over a filter selection the cap in words (decision 7870). */
  const head = selection ? `${count} · ${listSelectionCapSentence()}` : count;
  const actions = step === 'edit'
    ? [{ kind: 'primary' as const, label: t('bulkEdit.review'), onAct: () => void review(), disabled: busy || overCap }, { kind: 'cancel' as const, label: t('modal.cancel'), onAct: onCancel }]
    : step === 'confirm'
      ? [{ kind: 'primary' as const, label: t('bulkEdit.apply'), onAct: () => void apply(), disabled: busy || (field === 'attribute' && reviewed?.carrying.length === 0) }, { kind: 'cancel' as const, label: t('bulkEdit.back'), onAct: () => setStep('edit') }]
      : [{ kind: 'primary' as const, label: t('bulkEdit.done'), onAct: onCancel }, { kind: 'cancel' as const, label: t('bulkEdit.editAgain'), onAct: editAgain }];
  const value = chosen();
  return (
    <Modal open={open} title={t('bulkEdit.title')} subtitle={head} onCancel={onCancel} dataModal="bulk-edit" actions={actions}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      {overCap && !error ? <p className={styles.hint} data-bulk-edit-over-cap="true">{listSelectionTooManySentence(rowCount)}</p> : null}
      {step === 'edit' ? (
        <>
          <p className={styles.hint}>{t('bulkEdit.hint')}</p>
          <Select size={Size.Medium} label={t('bulkEdit.field')} value={field} onChange={(next) => { setField(next as BulkEditField); if (next === 'imageFileId' && imageMode === 'set' && !picture) setPicking(true); }} options={fields.map((f) => ({ value: f, label: t(BULK_EDIT_LABEL_KEYS[f]) }))} dataAttribute={{ 'data-bulk-edit-field': 'field' }} />
          {field === 'imageFileId' ? (
            <div className={styles.field} data-bulk-edit-field="imageFileId">
              <RadioGroup
                name="bulk-edit-image-mode"
                legend={t('bulkEdit.image.mode')}
                orientation="horizontal"
                value={imageMode}
                onChange={(next) => { setImageMode(next as ImageMode); if (next === 'set' && !picture) setPicking(true); }}
                options={[{ value: 'set', label: t('item.image.set') }, { value: 'remove', label: t('item.image.remove') }]}
              />
              {imageMode === 'set' ? (
                <>
                  <p className={styles.hint} data-bulk-edit-picture={picture ? 'chosen' : 'none'}>{picture ? `${t('bulkEdit.image.chosen')}: ${picture.name}` : t('bulkEdit.image.none')}</p>
                  <Button size={Size.Small} text={[picture ? t('bulkEdit.image.chooseAnother') : t('bulkEdit.image.choose')]} actions={[() => setPicking(true)]} />
                </>
              ) : null}
            </div>
          ) : field === 'supplier' ? (
            <div className={styles.field} data-bulk-edit-field="supplier">
              <p className={styles.hint}>{t('bulkEdit.supplier.hint')}</p>
              <Dropdown size={Size.Medium} label={t('item.supplier.company')} entityLabel={t('edit.companyEntity')} search={searchCompanies} value={vendor?.line1 ?? null} onSelect={setVendor} onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord', role: 'vendor' }, setVendor)} />
              <RadioGroup name="bulk-edit-supplier-status" legend={t('item.supplier.status')} orientation="horizontal" value={vendorStatus} onChange={(next) => setVendorStatus(next as BulkEditSupplierStatus)} options={SUPPLIER_STATUS_OPTIONS} />
              {vendorStatus === 'Primary' ? <p className={styles.hint} data-bulk-edit-supplier-primary="true">{t('bulkEdit.supplier.primaryHint')}</p> : null}
            </div>
          ) : field === 'attribute' ? (
            <div className={styles.field} data-bulk-edit-field="attribute">
              {attributeTypes.state.kind === 'ready' && typeRows.length === 0 ? <p className={styles.hint}>{t('bulkEdit.attribute.none')}</p> : (
                <Select size={Size.Large} label={t('item.attribute.type')} value={attr.typeId} onChange={(next) => setAttr({ typeId: next, unitId: typeRows.find((row) => row.id === next)?.defaultUnitId ?? '', entered: '', valueId: '' })} placeholder={t('bulkEdit.choose')} options={typeRows.map((row) => ({ value: row.id, label: row.attributeName }))} dataAttribute={{ 'data-bulk-edit-attribute': 'type' }} />
              )}
              {attrType?.kind === 'Enumerated' ? (
                <Select size={Size.Medium} label={t('item.attribute.value')} value={attr.valueId} onChange={(next) => setAttr({ ...attr, valueId: next })} placeholder={t('bulkEdit.choose')} options={attrType.values.filter((row) => row.isActive).map((row) => ({ value: row.id, label: row.value }))} dataAttribute={{ 'data-bulk-edit-attribute': 'value' }} />
              ) : null}
              {attrType?.kind === 'Measured' ? (
                <>
                  <TextInput size={Size.Small} label={t('item.attribute.value')} value={attr.entered} onChange={(next) => setAttr({ ...attr, entered: next })} name="bulk-edit-attribute-value" />
                  <Select size={Size.Medium} label={t('item.attribute.unit')} value={attr.unitId} onChange={(next) => setAttr({ ...attr, unitId: next })} placeholder={t('bulkEdit.choose')} options={attrUnits.map((row) => ({ value: row.id, label: row.label }))} dataAttribute={{ 'data-bulk-edit-attribute': 'unit' }} />
                </>
              ) : null}
            </div>
          ) : isBulkEditPriceField(field) ? (
            <NumericInput size={Size.Medium} kind="currency" label={label} value={typeof draft?.value === 'number' ? draft.value : null} onChange={(next) => setDraft({ value: next, text: null })} name="bulk-edit-price" />
          ) : field === 'status' ? (
            <Select size={Size.Medium} label={label} value={String(draft?.value ?? '')} onChange={(next) => setDraft({ value: next, text: STATUS_OPTIONS.find((o) => o.value === next)?.label ?? next })} placeholder={t('bulkEdit.choose')} options={STATUS_OPTIONS} dataAttribute={{ 'data-bulk-edit-field': 'status' }} />
          ) : isAccountField(field) ? (
            <Select key={field} size={Size.Medium} label={label} value={String(draft?.value ?? '')} onChange={(next) => setDraft({ value: next, text: accountOptions.find((o) => o.value === next)?.label ?? null })} placeholder={t('bulkEdit.choose')} options={accountOptions} dataAttribute={{ 'data-bulk-edit-field': field }} />
          ) : formField ? (
            <div key={field} className={styles.field} data-bulk-edit-field={field}>
              <RecordField
                field={formField}
                value={draft?.value ?? null}
                onChange={(next) => chooseFormValue(formField, next)}
                onLabel={(name) => setDrafts((all) => ({ ...all, [field]: { value: all[field]?.value ?? null, text: name } }))}
                options={formField.optionsFrom ? (lists[formField.optionsFrom] ?? []) : (formField.options ?? [])}
                apiBaseUrl={apiBaseUrl}
                countryCode="US"
              />
            </div>
          ) : (
            <div className={styles.field} data-bulk-edit-field="rep">
              <Dropdown size={Size.Medium} label={label} entityLabel={t('lead.dispatch.repEntity')} search={search} value={draft?.text ?? null} onSelect={(row: SuggestionRow) => setDraft({ value: row.id, text: row.line1 })} onAddNew={() => addNew.open({ kind: 'employee' }, (row: SuggestionRow) => setDraft({ value: row.id, text: row.line1 }))} />
            </div>
          )}
        </>
      ) : null}
      {step === 'confirm' && value ? (
        <p className={styles.hint} data-bulk-edit-confirm="true">{[t('bulkEdit.confirmPrefix'), field === 'attribute' ? attrType?.attributeName ?? label : label, t('bulkEdit.confirmTo'), value.text, t('bulkEdit.confirmOn'), `${field === 'attribute' && reviewed ? `${reviewed.carrying.length} ${t('bulkEdit.confirmOf')} ` : ''}${count}?`, t('bulkEdit.confirmEach')].join(' ')}</p>
      ) : null}
      {step === 'confirm' && field === 'attribute' && reviewed ? (
        <div data-bulk-edit-review="attribute">
          {reviewed.carrying.length === 0 ? <p className={styles.hint} data-bulk-edit-carrying="none">{t('bulkEdit.attribute.nobody')}</p> : (
            <>
              <p className={styles.hint}>{[t('bulkEdit.attribute.carrying'), `(${reviewed.carrying.length})`].join(' ')}</p>
              <ul className={styles.hint} data-bulk-edit-carrying="true">{reviewed.carrying.map((row) => <li key={row.id} data-bulk-edit-carrying-row={row.id}>{row.label}</li>)}</ul>
            </>
          )}
          {reviewed.lacking.length > 0 ? (
            <>
              <p className={styles.hint}>{[t('bulkEdit.attribute.lacking'), `(${reviewed.lacking.length})`].join(' ')}</p>
              <ul className={styles.hint} data-bulk-edit-lacking="true">{reviewed.lacking.map((row) => <li key={row.id} data-bulk-edit-lacking-row={row.id}>{row.label}</li>)}</ul>
            </>
          ) : null}
        </div>
      ) : null}
      {step === 'result' && answer ? (
        <div data-bulk-edit-result="true">
          {/* 🔨 A banner stating a problem offers an action (piece 3): a refused row can be tried again with another value. */}
          {answer.refused.length === 0 ? (
            <AlertBanner tone="good" message={`${answer.accepted} ${t('bulkEdit.changed')}`} />
          ) : (
            <AlertBanner tone="warning" message={`${answer.accepted} ${t('bulkEdit.changed')} · ${answer.refused.length} ${t('bulkEdit.refusedCount')}`} action={{ label: t('bulkEdit.editAgain'), onAction: editAgain }} />
          )}
          {answer.refused.length > 0 ? (
            <ul className={styles.hint} data-bulk-edit-refused="true">
              {answer.refused.map((row) => (
                <li key={row.id} data-bulk-edit-refused-row={row.id}>{[row.label, row.reason].join(REASON_SEPARATOR)}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {/* 🔒 The Item Image modal opens once to pick the picture and uploads nothing; unmounted when closed so each opening starts clean (decision 7640). */}
      {picking ? (
        <ItemImageModal open itemId="" apiBaseUrl={apiBaseUrl} onCancel={() => setPicking(false)} onSaved={NOTHING_SAVED} onPick={setPicture} title={t('bulkEdit.image.modalTitle')} saveLabel={t('bulkEdit.image.use')} dataModal="bulk-edit-image" />
      ) : null}
      {addNew.host}
    </Modal>
  );
}
